import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {HttpBackend, HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';

interface BuildVersionInfo {
    sha: string;
    deployedAt?: string;
}

/**
 * Compara el SHA con el que arrancó esta pestaña (meta `build-sha` de `index.html`, que
 * sustituye `deploy_cnc.sh` tras el `ng build`) contra el que el servidor publica AHORA
 * (`version.json`, generado en el mismo paso del deploy y desde la misma variable, así que
 * meta y json de un mismo despliegue no pueden desincronizarse).
 *
 * Solo AVISA — nunca recarga por su cuenta: cuándo se recarga lo decide la persona que está
 * delante, que puede tener un pedido a medio meter.
 *
 * El `setInterval` va fuera de la zona de Angular: dentro, un temporizador que no para nunca
 * mantiene la zona perpetuamente inestable. Solo se reentra en la zona cuando el estado
 * publicado cambia de verdad, que es lo único que la vista necesita repintar.
 */
@Injectable({providedIn: 'root'})
export class BuildVersionService implements OnDestroy {
    /** Debe coincidir con el placeholder de `src/index.html`. */
    private static readonly PLACEHOLDER = '__BUILD_SHA__';
    public static readonly POLL_MS = 10 * 60_000;

    /** SHA publicado por el servidor cuando no es el que tiene cargado esta pestaña; `null` si van iguales. */
    public readonly nuevaVersion = new BehaviorSubject<string | null>(null);

    private readonly _shaPropio: string | null;
    private readonly _http: HttpClient;
    private _sondeo: ReturnType<typeof setInterval> | undefined;

    constructor(httpBackend: HttpBackend, private readonly _zone: NgZone) {
        // Directo al backend, saltándose TODOS los interceptores. Es trabajo de fondo y no
        // puede pasar por LoadingInterceptor: ese enciende `.cargando_overlay`, una capa a
        // pantalla completa (z-index 9999) que se ve justo en pedidos, solicitudes y
        // producción y se queda un segundo más al terminar. Cada comprobación haría
        // parpadear las vistas críticas y se tragaría el clic que cayera en ese instante.
        this._http = new HttpClient(httpBackend);
        this._shaPropio = this._leerShaPropio();
        if (!this._shaPropio) {
            // Sin SHA propio no hay con qué comparar: `ng serve` en local, o una pestaña que
            // sigue con un index.html anterior a que esto existiera. No se hace NINGUNA
            // petición, así que no cabe un falso "hay versión nueva".
            return;
        }
        this._zone.runOutsideAngular(() => {
            this._comprobar();
            this._sondeo = setInterval(() => {
                if (document.hidden) {
                    return;
                }
                this._comprobar();
            }, BuildVersionService.POLL_MS);
        });
    }

    public ngOnDestroy(): void {
        clearInterval(this._sondeo);
    }

    private _leerShaPropio(): string | null {
        const contenido = document.querySelector('meta[name="build-sha"]')?.getAttribute('content');
        if (!contenido || contenido === BuildVersionService.PLACEHOLDER) {
            return null;
        }
        return contenido;
    }

    private _comprobar(): void {
        const headers = new HttpHeaders({'Cache-Control': 'no-cache'});
        this._http.get<BuildVersionInfo>(`version.json?_=${Date.now()}`, {headers}).subscribe({
            next: info => {
                if (!info?.sha) {
                    // JSON sin `sha` (respuesta corrupta o inesperada): mismo trato que un
                    // error de red, no se toca el último estado conocido.
                    return;
                }
                this._publicar(info.sha === this._shaPropio ? null : info.sha);
            },
            error: () => {
                // Red caída, o el 404 transitorio de mientras el propio deploy sube
                // version.json. Ni inventa un aviso ni borra uno real.
            },
        });
    }

    private _publicar(sha: string | null): void {
        if (sha === this.nuevaVersion.value) {
            return;
        }
        this._zone.run(() => this.nuevaVersion.next(sha));
    }
}
